import { useState } from "react";
import { Dropdown } from "../../Dropdown";

function MismatchPanel({ recipe, householdSize, currentDayCode, onResolve }) {
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const mealTypes = ["Breakfast", "Lunch", "Dinner", "Snack"];
  const availableDays = days.slice(currentDayCode + 1);

  const [leftoverDay, setLeftoverDay] = useState("");
  const [leftoverMeal, setLeftoverMeal] = useState("");

  const tooMany = recipe.servings > householdSize;

  if (tooMany) {
    return (
      <div className="border-t border-gray-200 bg-gray-100 px-4 py-3">
        <p className="mb-2 text-xs text-gray-500">
          Makes {recipe.servings} servings — save the extra as a leftover?
        </p>
        <div className="mb-2 flex gap-2">
          <Dropdown
            options={availableDays}
            selectedOption={leftoverDay || "Day..."}
            onSelect={setLeftoverDay}
            className="flex-1"
          />
          <Dropdown
            options={mealTypes}
            selectedOption={leftoverMeal || "Meal..."}
            onSelect={setLeftoverMeal}
            className="flex-1"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onResolve(recipe, "leftover", { day: leftoverDay.toUpperCase(), meal: leftoverMeal.toUpperCase() })}
            disabled={!leftoverDay || !leftoverMeal}
            className="flex-1 rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
          >
            + Leftover
          </button>
          <button
            onClick={() => onResolve(recipe, "normal")}
            className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            Skip
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="border-t border-gray-200 bg-gray-100 px-4 py-3">
      <p className="mb-2 text-xs text-gray-500">
        Only {recipe.servings} serving{recipe.servings !== 1 ? "s" : ""} — double ingredients for your household of {householdSize}?
      </p>
      <div className="flex gap-2">
        <button
          onClick={() => onResolve(recipe, "double")}
          className="flex-1 rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-800"
        >
          Double
        </button>
        <button
          onClick={() => onResolve(recipe, "normal")}
          className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
        >
          Skip
        </button>
      </div>
    </div>
  );
}

export function AddMealToPlanModalRecipeList({
  filteredRecipes,
  selectedRecipes,
  handleSelectRecipe,
  getMealDisplayInfo,
  mismatchState,
  onResolveMismatch,
  householdSize,
  currentDayCode,
  currentMealCode,
}) {
  return (
    <div className="flex-1 overflow-y-auto px-6 py-3">
      {filteredRecipes.length > 0 ? (
        <div className="divide-y divide-gray-200 overflow-hidden rounded-lg border border-gray-200 bg-white">
          {filteredRecipes.map((recipe) => {
            const isSelected = selectedRecipes.some((r) => r.mealId === recipe.mealId);
            const mismatch = mismatchState[recipe.mealId];
            const isPendingResolution = isSelected && mismatch && !mismatch.resolved;
            const displayInfo = isSelected && !isPendingResolution ? getMealDisplayInfo(recipe) : null;

            return (
              <div key={recipe.mealId}>
                <div
                  onClick={() => handleSelectRecipe(recipe)}
                  className={`flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors ${
                    isSelected ? "bg-gray-50" : "hover:bg-gray-50"
                  }`}
                >
                  <div
                    className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded ${
                      isSelected ? "bg-gray-900 text-white" : "border-2 border-gray-300"
                    }`}
                  >
                    {isSelected && (
                      <svg className="h-3 w-3" fill="none" viewBox="0 0 12 12">
                        <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>

                  <div className="flex flex-1 items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{recipe.mealName}</p>
                      <p className="mt-0.5 text-xs text-gray-500">
                        {recipe.servings ?? 2} servings
                        {isPendingResolution && (
                          <span className="ml-2 text-amber-600">· serving mismatch</span>
                        )}
                      </p>
                    </div>
                    {displayInfo && (
                      <span
                        className={`flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                          displayInfo.color === "blue"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-green-100 text-green-700"
                        }`}
                      >
                        {displayInfo.badge}
                      </span>
                    )}
                  </div>
                </div>

                {isPendingResolution && (
                  <MismatchPanel
                    recipe={recipe}
                    householdSize={householdSize}
                    currentDayCode={currentDayCode}
                    currentMealCode={currentMealCode}
                    onResolve={onResolveMismatch}
                  />
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="py-8 text-center">
          <p className="text-gray-500">No recipes found</p>
          <p className="mt-1 text-sm text-gray-400">Try a different search term</p>
        </div>
      )}
    </div>
  );
}
